import { useEffect, useRef, useState } from 'react';

/**
 * Creates a value exactly once, after `ready` settles, and returns it (`null`
 * until then).
 *
 * Built for each app's data router (PSTUDIO-4288): `Sentry.wrapCreateBrowserRouter`
 * reads its tracing bindings when it wraps, so the router has to be created after
 * `Sentry.init` — which waits on LaunchDarkly — or it is never instrumented.
 * `ready` is the promise `initSentryWithLaunchDarklyGate` returns.
 *
 * "Exactly once" is held by a ref rather than inside a state updater, which
 * StrictMode may call twice; an unmount before `ready` settles creates nothing,
 * and a rejected `ready` still creates (the gate never rejects, but a blank app
 * is the wrong failure).
 */
export const useCreateOnceAfter = <T>(
  ready: Promise<void>,
  create: () => T
): T | null => {
  const created = useRef<T | null>(null);
  const createRef = useRef(create);
  const [value, setValue] = useState<T | null>(null);
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);

  useEffect(() => {
    let active = true;

    const run = () => {
      if (!active) return;
      try {
        if (created.current === null) created.current = createRef.current();
        setValue(created.current);
      } catch (error) {
        // Thrown from a promise callback it would be an unhandled rejection
        // and a blank app; rethrown in render, an error boundary catches it.
        setFailure({ error });
      }
    };
    // Fail open: a rejected `ready` must not leave the app without its router.
    ready.then(run, run);

    return () => {
      active = false;
    };
  }, [ready]);

  if (failure) throw failure.error;

  return value;
};
